import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject } from "@/shared/utils/typeGuards";

/**
 * Google DeepMind's MIDI-conditioned audio API ("Lyria"). Three calls: transcribe audio to a note
 * sequence, edit audio so it follows a changed note sequence, and generate audio from a note
 * sequence plus a text prompt. Every time here is in seconds.
 */

export const kLyriaTranscribeRoute = "/lyria/transcribe";
export const kLyriaNoteEditRoute = "/lyria/note-edit";
export const kLyriaNoteInstructRoute = "/lyria/note-instruct";

/** The model generates one window of this length; note content past it is dropped. */
export const kLyriaWindow_s = 39.7;

/** The longest input sent for transcription or editing. The service has about 2 minutes per call. */
export const kLyriaMaxInputDuration_s = 120;

/**
 * The most notes in one sequence. A note edit sends two, and each note is about 80 bytes of JSON
 * with times rounded to a millisecond, so two full sequences stay well under the server's 1 MB
 * request body limit.
 */
export const kLyriaMaxNotes = 4000;
export const kLyriaMaxEditRegions = 64;
export const kLyriaMaxPromptLength = 1000;

/** The transcriber's timing grid: 25 frames a second. Every note boundary it returns is on it. */
export const kLyriaFrame_s = 0.04;

/** The largest seed the API takes (a positive int32). A negative or absent seed means random. */
export const kLyriaSeedMax = 2_147_483_647;

/**
 * DeepMind's own defaults (the "MIDI-Conditioned Audio Generation API" reference, request tables
 * for GenerateNoteEditAudio and GenerateNoteInstructAudio), which apply whenever a value is left
 * out of the request.
 */
export const kLyriaNoteEditDefaults = { cfgScale: 1.5, numSteps: 50 } as const;
export const kLyriaNoteInstructDefaults = { cfgScale: 1.75, numSteps: 20 } as const;

/** The ranges DeepMind calls useful. The server accepts wider ones; these bound the UI. */
export const kLyriaCfgScaleRange = { min: 1, max: 3, step: 0.05 } as const;
export const kLyriaNumStepsRange = { min: 10, max: 50, step: 1 } as const;
export const kLyriaEditTMaxRange = { min: 0, max: 1, step: 0.05 } as const;

/**
 * How much timing noise goes into the notes before Lyria renders them. DeepMind's workaround for
 * output that sounds "too MIDI" (kickoff call, 2026-10-14): nudge onsets and stretch durations.
 * They expect to fix it in the model, at which point the default goes to 0. Ours, not DeepMind's.
 */
export const kLyriaHumanizeRange = { min: 0, max: 1, step: 0.05 } as const;
export const kLyriaDefaultHumanize = 0.5;

/**
 * What a caller may tune on a Lyria generation. Every field is optional: absent means DeepMind's
 * default, and an absent seed means a new random one. `editTMax` only applies to a note edit.
 * `humanize` is applied to the notes in the browser and never sent; absent means
 * `kLyriaDefaultHumanize`.
 */
export interface LyriaTuning {
    seed?: number;
    cfgScale?: number;
    numSteps?: number;
    editTMax?: number;
    humanize?: number;
}

export interface LyriaNote {
    pitch: number; // MIDI pitch, 0-127
    startTime: number;
    endTime: number;
    velocity: number; // 1-127; the model only reads it as on or off
}

export interface LyriaNoteSequence {
    notes: LyriaNote[];
    totalTime?: number;
}

export interface LyriaEditRegion {
    startSecs: number;
    endSecs: number;
}

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

export const isLyriaNote = (value: unknown): value is LyriaNote =>
    isObject<LyriaNote>(value) &&
    Number.isInteger(value.pitch) &&
    value.pitch >= 0 &&
    value.pitch <= 127 &&
    isFiniteNumber(value.startTime) &&
    value.startTime >= 0 &&
    isFiniteNumber(value.endTime) &&
    value.endTime > value.startTime &&
    Number.isInteger(value.velocity) &&
    value.velocity >= 1 &&
    value.velocity <= 127;

export const isLyriaNoteSequence = (value: unknown): value is LyriaNoteSequence =>
    isObject<LyriaNoteSequence>(value) &&
    Array.isArray(value.notes) &&
    value.notes.every(isLyriaNote) &&
    (value.totalTime === undefined || (isFiniteNumber(value.totalTime) && value.totalTime >= 0));

const isOptionalFiniteNumber = (value: unknown): boolean =>
    value === undefined || isFiniteNumber(value);

export const isLyriaTuning = (value: unknown): value is LyriaTuning =>
    isObject<LyriaTuning>(value) &&
    isOptionalFiniteNumber(value.seed) &&
    isOptionalFiniteNumber(value.cfgScale) &&
    isOptionalFiniteNumber(value.numSteps) &&
    isOptionalFiniteNumber(value.editTMax) &&
    isOptionalFiniteNumber(value.humanize);

/** The smallest seed sent. The API reads a negative or absent seed as random; 0 is left alone too. */
export const kLyriaSeedMin = 1;

/** A seed the API treats as fixed: a random positive int32. */
export const createLyriaSeed = (random: () => number = Math.random): number =>
    kLyriaSeedMin + Math.floor(random() * (kLyriaSeedMax - kLyriaSeedMin));

const clampNumber = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

/**
 * A saved tuning as the routes accept it: integers where the API wants them, every value in the
 * server's range, anything else dropped. Saved state is the only way an odd value gets here.
 */
export const sanitizeLyriaTuning = (tuning: LyriaTuning): LyriaTuning => {
    const result: LyriaTuning = {};
    if (tuning.seed !== undefined) {
        result.seed = clampNumber(Math.round(tuning.seed), kLyriaSeedMin, kLyriaSeedMax);
    }
    if (tuning.cfgScale !== undefined) {
        result.cfgScale = clampNumber(
            tuning.cfgScale,
            kLyriaCfgScaleRange.min,
            kLyriaCfgScaleRange.max,
        );
    }
    if (tuning.numSteps !== undefined) {
        result.numSteps = clampNumber(
            Math.round(tuning.numSteps),
            kLyriaNumStepsRange.min,
            kLyriaNumStepsRange.max,
        );
    }
    if (tuning.editTMax !== undefined) {
        result.editTMax = clampNumber(
            tuning.editTMax,
            kLyriaEditTMaxRange.min,
            kLyriaEditTMaxRange.max,
        );
    }
    if (tuning.humanize !== undefined) {
        result.humanize = clampNumber(
            tuning.humanize,
            kLyriaHumanizeRange.min,
            kLyriaHumanizeRange.max,
        );
    }
    return result;
};

/** Notes closer than this touch: the end of one is the start of the next, to float error. */
const kTouching_s = 0.001;

/**
 * The sequence with notes the transcriber split rejoined. It analyses audio in fixed segments,
 * and a note held across a segment boundary comes back as two notes of the same pitch, the
 * second starting exactly where the first ends.
 *
 * NOT APPLIED to what the server returns: a legato re-strike of the same pitch may also come
 * back touching, and then this would hide an onset Lyria heard. Until a probe with a known
 * sustain and a known re-strike shows where the segment edges fall, the transcription is kept
 * as returned, which is what DeepMind says the reference for a note edit must be.
 */
export const mergeSplitNotes = (sequence: LyriaNoteSequence): LyriaNoteSequence => {
    const byPitch = new Map<number, LyriaNote[]>();
    for (const note of sequence.notes) {
        byPitch.set(note.pitch, [...(byPitch.get(note.pitch) ?? []), note]);
    }
    const merged: LyriaNote[] = [];
    for (const notes of byPitch.values()) {
        notes.sort((a, b) => a.startTime - b.startTime);
        for (const note of notes) {
            const last = merged.at(-1);
            if (
                last?.pitch === note.pitch &&
                Math.abs(note.startTime - last.endTime) <= kTouching_s
            ) {
                last.endTime = Math.max(last.endTime, note.endTime);
            } else {
                merged.push({ ...note });
            }
        }
    }
    merged.sort((a, b) => a.startTime - b.startTime || a.pitch - b.pitch);
    return { ...sequence, notes: merged };
};

export const isLyriaEditRegion = (value: unknown): value is LyriaEditRegion =>
    isObject<LyriaEditRegion>(value) &&
    isFiniteNumber(value.startSecs) &&
    value.startSecs >= 0 &&
    isFiniteNumber(value.endSecs) &&
    value.endSecs > value.startSecs;

/** Why a sequence cannot be sent, or undefined when it can. */
export const getLyriaSequenceError = (value: unknown, label: string): string | undefined => {
    if (!isLyriaNoteSequence(value)) {
        return `${label} is not a valid note sequence (pitch 0-127, velocity 1-127, end after start)`;
    }
    if (value.notes.length === 0) {
        return `${label} has no notes`;
    }
    if (value.notes.length > kLyriaMaxNotes) {
        return `${label} has more than ${String(kLyriaMaxNotes)} notes`;
    }
    return undefined;
};

/**
 * The sequence as the generation window sees it: notes starting after the window are dropped, notes
 * crossing its end are cut at it, and `totalTime` covers the notes. Undefined when nothing is left.
 */
export const clampSequenceToWindow = (
    sequence: LyriaNoteSequence,
    window_s: number = kLyriaWindow_s,
): LyriaNoteSequence | undefined => {
    const notes = sequence.notes
        .filter((note) => note.startTime < window_s)
        .map((note) => ({ ...note, endTime: Math.min(note.endTime, window_s) }));
    if (notes.length === 0) {
        return undefined;
    }
    const lastEnd = Math.max(...notes.map((note) => note.endTime));
    return { notes, totalTime: Math.min(Math.max(sequence.totalTime ?? 0, lastEnd), window_s) };
};

const roundMs = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * The sequence with every time rounded to a millisecond, which keeps the request small. A note
 * never loses its length to the rounding, and `totalTime` still covers the last note.
 */
export const roundLyriaSequenceTimes = (sequence: LyriaNoteSequence): LyriaNoteSequence => {
    const notes = sequence.notes.map((note) => {
        const startTime = roundMs(note.startTime);
        return {
            ...note,
            startTime,
            endTime: Math.max(roundMs(note.endTime), roundMs(startTime + 0.001)),
        };
    });
    const lastEnd = Math.max(0, ...notes.map((note) => note.endTime));
    return {
        notes,
        totalTime:
            sequence.totalTime === undefined
                ? undefined
                : Math.max(roundMs(sequence.totalTime), lastEnd),
    };
};

export interface LyriaTranscribeRequestBody {
    fileKey: string; // S3 key of the audio to transcribe
}

export interface LyriaNoteEditRequestBody {
    fileKey: string; // S3 key of the audio to edit
    originalNoteSequence: LyriaNoteSequence; // from `/lyria/transcribe` on this same audio
    editedNoteSequence: LyriaNoteSequence;
    caption?: string; // the source timbre; generated server-side when absent
    editRegions?: LyriaEditRegion[];
    seed?: number;
    cfgScale?: number;
    numSteps?: number;
    editTMax?: number; // 0-1; higher allows larger departures from the source
}

export interface LyriaNoteInstructRequestBody {
    noteSequence: LyriaNoteSequence;
    prompt: string;
    durationSeconds?: number;
    seed?: number;
    cfgScale?: number;
    numSteps?: number;
}

export interface LyriaTranscribeSuccessResponse {
    ok: true;
    noteSequence: LyriaNoteSequence;
}

export interface LyriaAudioSuccessResponse {
    ok: true;
    audioUrl: string; // presigned URL of a 48 kHz stereo 16-bit WAV
}

export const isLyriaTranscribeRequestBody = (value: unknown): value is LyriaTranscribeRequestBody =>
    isObject(value) && typeof value.fileKey === "string";

const isOptional = (value: unknown, check: (v: unknown) => boolean): boolean =>
    value === undefined || check(value);

export const isLyriaNoteEditRequestBody = (value: unknown): value is LyriaNoteEditRequestBody =>
    isObject<LyriaNoteEditRequestBody>(value) &&
    typeof value.fileKey === "string" &&
    isLyriaNoteSequence(value.originalNoteSequence) &&
    isLyriaNoteSequence(value.editedNoteSequence) &&
    isOptional(value.caption, (v) => typeof v === "string") &&
    isOptional(value.editRegions, (v) => Array.isArray(v) && v.every(isLyriaEditRegion)) &&
    isOptional(value.seed, isFiniteNumber) &&
    isOptional(value.cfgScale, isFiniteNumber) &&
    isOptional(value.numSteps, isFiniteNumber) &&
    isOptional(value.editTMax, isFiniteNumber);

export const isLyriaNoteInstructRequestBody = (
    value: unknown,
): value is LyriaNoteInstructRequestBody =>
    isObject<LyriaNoteInstructRequestBody>(value) &&
    isLyriaNoteSequence(value.noteSequence) &&
    typeof value.prompt === "string" &&
    isOptional(value.durationSeconds, isFiniteNumber) &&
    isOptional(value.seed, isFiniteNumber) &&
    isOptional(value.cfgScale, isFiniteNumber) &&
    isOptional(value.numSteps, isFiniteNumber);

export const isLyriaTranscribeSuccessResponse = (
    value: unknown,
): value is LyriaTranscribeSuccessResponse =>
    isObject(value) && value.ok === true && isLyriaNoteSequence(value.noteSequence);

export const isLyriaAudioSuccessResponse = (value: unknown): value is LyriaAudioSuccessResponse =>
    isObject(value) && value.ok === true && typeof value.audioUrl === "string";

export const kLyriaTranscribeEndpoint: ApiEndpoint<
    LyriaTranscribeRequestBody,
    LyriaTranscribeSuccessResponse
> = {
    route: kLyriaTranscribeRoute,
    label: "Lyria Transcribe",
    source: "DeepMind",
    isEvaluationOnly: true,
    category: "3P GenAI",
    description:
        "Transcribes WAV audio to a note sequence with DeepMind's Lyria 4 transcriber. Built as the first step of Lyria 4 Note Edit; 40 ms timing grid, no velocity.",
    isRequestBody: isLyriaTranscribeRequestBody,
    isSuccessResponse: isLyriaTranscribeSuccessResponse,
};

export const kLyriaNoteEditEndpoint: ApiEndpoint<
    LyriaNoteEditRequestBody,
    LyriaAudioSuccessResponse
> = {
    route: kLyriaNoteEditRoute,
    label: "Lyria Note Edit",
    source: "DeepMind",
    isEvaluationOnly: true,
    category: "3P GenAI",
    description:
        "Re-renders audio so it follows an edited note sequence, keeping the rest of the sound, with DeepMind's Lyria 4. Output is the same length as the input.",
    isRequestBody: isLyriaNoteEditRequestBody,
    isSuccessResponse: isLyriaAudioSuccessResponse,
};

export const kLyriaNoteInstructEndpoint: ApiEndpoint<
    LyriaNoteInstructRequestBody,
    LyriaAudioSuccessResponse
> = {
    route: kLyriaNoteInstructRoute,
    label: "Lyria Note Instruct",
    source: "DeepMind",
    isEvaluationOnly: true,
    category: "3P GenAI",
    description:
        "Generates audio from a note sequence and a text prompt describing the sound, with DeepMind's Lyria 4. One window of about 40 seconds.",
    isRequestBody: isLyriaNoteInstructRequestBody,
    isSuccessResponse: isLyriaAudioSuccessResponse,
};
